// @flow
import * as React from 'react';
import type { RecipeT, RecipeText, TagT, RecipeStatus, CommentT } from '../collections';
import type { Client, Collection } from '../../../packages/client-bundle';
import { useCollection, useItem } from '../../../packages/client-react';

import Button from '@material-ui/core/Button';
import { Route, Link, useRouteMatch, useParams, useHistory } from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import EditIcon from '@material-ui/icons/Edit';
import Star from '@material-ui/icons/Star';
import Close from '@material-ui/icons/Close';
import StarOutline from '@material-ui/icons/StarOutline';
import { makeStyles } from '@material-ui/core/styles';
import deepEqual from '@birchill/json-equalish';
import renderQuill from './renderQuill';
import { imageUrl } from './utils';
import TagsEditor from './TagsEditor';

const Stars = ({ value, onChange }) => {
    const [hover, setHover] = React.useState(null);
    return (
        <div style={{ display: 'flex' }}>
            {[1, 2, 3, 4, 5].map((num) => (
                <div
                    onMouseEnter={() => setHover(num)}
                    style={{ cursor: 'pointer' }}
                    onMouseLeave={() =>
                        setTimeout(() => setHover((at) => (at === num ? null : at)), 50)
                    }
                    key={num}
                    onClick={() => onChange(num === value ? null : num)}
                >
                    {(hover != null ? num <= hover : num <= value) ? (
                        <Star color={hover != null ? 'primary' : undefined} />
                    ) : (
                        <StarOutline />
                    )}
                </div>
            ))}
        </div>
    );
};

export const NewComment = ({
    recipe,
    col,
    actorId,
    url,
}: {
    recipe: RecipeT,
    col: Collection<RecipeT>,
    actorId: string,
    url: string,
}) => {
    const [text, setText] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [happiness, setHappiness] = React.useState(0);
    const [images, setImages] = React.useState<Array<string | Blob>>([]);

    if (text == null) {
        return <Button onClick={() => setText('')}>Add comment</Button>;
    }

    return (
        <div style={{ padding: 8 }}>
            <Stars
                value={happiness}
                onChange={(value) => {
                    setHappiness(value == null ? 0 : value);
                }}
            />
            <div style={{ height: 16 }} />
            <TextField
                multiline
                fullWidth
                variant="outlined"
                label="Comment"
                placeholder="Write your comment"
                value={text}
                onChange={(evt) => setText(evt.target.value)}
            />
            <ImageUploader url={url} value={images} onChange={setImages} />
            <div style={{ height: 16 }} />
            <Button
                variant="contained"
                disabled={loading}
                style={{ marginRight: 16 }}
                onClick={async () => {
                    setLoading(true);
                    const id = col.genId();

                    const uploadedImages = await Promise.all(
                        images.map((image) => uploadImage(url, id, image)),
                    );
                    const successfulImages: Array<string> = uploadedImages.filter(Boolean);

                    await col.setAttribute(recipe.id, ['comments', id], {
                        id,
                        authorId: actorId,
                        text: { ops: [{ insert: text.trim() + '\n' }] },
                        date: Date.now(),
                        happiness,
                        images: successfulImages,
                    });
                    setText(null);
                    setHappiness(0);
                    setLoading(false);
                }}
            >
                Save
            </Button>
            <Button onClick={() => setText(null)}>Cancel</Button>
        </div>
    );
};

const genId = () => Math.random().toString(36).slice(2);

export const EditComment = ({
    comment,
    onSave,
    onCancel,
    url,
}: {
    comment: CommentT,
    onSave: (RecipeText, number, Array<string>) => mixed,
    onCancel: () => mixed,
    url: string,
}) => {
    const [text, setText] = React.useState(deltasToString(comment.text.ops).trim());
    const [happiness, setHappiness] = React.useState(comment.happiness);
    const [images, setImages] = React.useState<Array<string | Blob>>(comment.images.map((x) => x));

    return (
        <div style={{ padding: 8 }}>
            <Stars
                value={happiness}
                onChange={(value) => {
                    setHappiness(value == null ? 0 : value);
                }}
            />
            <div style={{ height: 16 }} />
            <TextField
                multiline
                fullWidth
                variant="outlined"
                label="Comment"
                placeholder="Write your comment"
                value={text}
                onChange={(evt) => setText(evt.target.value)}
            />
            <ImageUploader url={url} value={images} onChange={setImages} />
            <div style={{ height: 16 }} />
            <Button
                variant="contained"
                style={{ marginRight: 16 }}
                onClick={async () => {
                    const uploadedImages = await Promise.all(
                        images.map((image) => uploadImage(url, comment.id, image)),
                    );
                    const successfulImages: Array<string> = uploadedImages.filter(Boolean);
                    onSave({ ops: [{ insert: text.trim() + '\n' }] }, happiness, successfulImages);
                }}
            >
                Save
            </Button>
            <Button onClick={() => onCancel()}>Cancel</Button>
        </div>
    );
};

type UploadableImage = string | Blob;

export const uploadImage = async (
    url: string,
    commentId: string,
    image: UploadableImage,
): Promise<?string> => {
    if (typeof image === 'string') {
        return image;
    }
    const path = `images/${commentId}/${genId()}.jpg`;
    const res = await fetch(`${url}/uploads/${path}`, {
        method: 'POST',
        body: image,
    });
    if (res.status >= 300 || res.status < 200) {
        console.log(new Error(`Failed to upload!`));
        console.log(res.status);
        console.log(await res.text());
        return null;
    }
    return `foood://${path}`;
};

const deltasToString = (ops) =>
    ops.map((op) => (typeof op.insert === 'string' ? op.insert : '')).join('');

const shrinkImage = (blob: Blob) => {
    const body = document.body;
    if (!body) {
        throw new Error('No body, cant shrink');
    }
    return new Promise((res, rej) => {
        setTimeout(() => rej(new Error('timeout')), 1000);
        console.log('shrinking', blob.size);
        var image = document.createElement('img');
        image.style.display = 'none';
        body.appendChild(image);
        image.onload = () => {
            var area = image.naturalWidth * image.naturalHeight;
            // shoot for 700k
            var resizeRatio = (0.7 * 1024 * 1024) / blob.size;
            var newArea = area * resizeRatio;
            var wToh = image.naturalWidth / image.naturalHeight;
            var newH = Math.sqrt(newArea / wToh);
            var newW = wToh * newH;
            var canvas = document.createElement('canvas');
            body.appendChild(canvas);
            canvas.style.display = 'none';
            canvas.width = newW;
            canvas.height = newH;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0, newW, newH);
            canvas.toBlob(
                (blob) => {
                    canvas.parentNode?.removeChild(canvas);
                    image.parentNode?.removeChild(image);
                    res(blob);
                },
                'image/jpeg',
                0.9,
            );
        };
        image.src = URL.createObjectURL(blob);
    });
};

const BlobImg = ({ src, url, ...props }) => {
    const srcUrl = React.useMemo(() => {
        if (typeof src === 'string') {
            return imageUrl(src, url);
        }
        return URL.createObjectURL(src);
    }, [src]);
    return <img {...props} src={srcUrl} />;
};

const oneMeg = 1024 * 1024;
const ensureSmallEnough = (blob) => {
    if (blob.size < oneMeg) {
        console.log(blob.size);
        return Promise.resolve(blob);
    } else {
        return shrinkImage(blob);
    }
};

export const ImageUploader = ({
    url,
    value,
    onChange,
    multiple = true,
    buttonLabel = 'Add Images',
}: {
    url: string,
    value: Array<UploadableImage>,
    onChange: (Array<UploadableImage>) => mixed,
    multiple?: boolean,
    buttonLabel?: string,
}) => {
    const targetRef = React.useRef(null);
    return (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {value.map((item, i) => (
                    <div
                        key={i}
                        style={{
                            marginRight: 16,
                            marginBottom: 16,
                            position: 'relative',
                        }}
                    >
                        <BlobImg
                            src={item}
                            url={url}
                            style={{
                                width: 200,
                                objectFit: 'cover',
                                height: 200,
                            }}
                        />
                        <IconButton
                            style={{ position: 'absolute', top: 8, right: 8 }}
                            color="inherit"
                            onClick={() => {
                                onChange(value.filter((v) => v !== item));
                            }}
                        >
                            <Close />
                        </IconButton>
                    </div>
                ))}
            </div>
            <input
                type="file"
                multiple={multiple}
                accept="image/*"
                style={{ display: 'none' }}
                ref={(node) => (targetRef.current = node)}
                onChange={async (evt) => {
                    console.log(evt.target.files);
                    const images = await Promise.all(
                        [...evt.target.files].map((blob) => ensureSmallEnough(blob)),
                    );
                    onChange(multiple ? value.concat(images) : images);
                }}
            />
            <Button
                variant="outlined"
                onClick={() => {
                    targetRef.current?.click();
                }}
            >
                {buttonLabel}
            </Button>
        </div>
    );
};
